import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { MessageSquarePlus, X, Star } from 'lucide-react'
import { supportApi } from '../../lib/api'
import { apiError } from '../../lib/errors'

// Floating "Send feedback" widget, shown on every authed tenant route (§221). Feedback is
// filed as a support_ticket with kind='feedback' + a rating + the page it came from, so it
// lands in the same case-management pipeline as support requests (staff queue + threaded
// replies + status). The grower can follow the thread on the Support page.

const CATS = [
  { key: 'bug', label: 'Bug', emoji: '🐞' },
  { key: 'feature_request', label: 'Idea', emoji: '💡' },
  { key: 'praise', label: 'Praise', emoji: '💚' },
  { key: 'other', label: 'Other', emoji: '💬' },
]

export function FeedbackWidget() {
  const [open, setOpen] = useState(false)
  const [cat, setCat] = useState('other')
  const [rating, setRating] = useState(0)
  const [body, setBody] = useState('')
  const [done, setDone] = useState(false)

  const submit = useMutation({
    mutationFn: () => {
      const catLabel = CATS.find(c => c.key === cat)?.label ?? 'Feedback'
      const subject = `Feedback: ${catLabel}` + (body.trim() ? ` — ${body.trim().slice(0, 60)}` : '')
      return supportApi.create({
        kind: 'feedback',
        category: cat,
        rating: rating > 0 ? rating : null,
        page_url: window.location.pathname + window.location.search,
        subject,
        body: body.trim(),
        priority: 'medium',
      })
    },
    onSuccess: () => setDone(true),
  })

  function reset() {
    setOpen(false); setDone(false); setCat('other'); setRating(0); setBody('')
    submit.reset()
  }

  return (
    <>
      {/* Launcher — bottom-right so it clears the bottom-center offline pill. */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        title="Send feedback"
        style={{
          position: 'fixed', right: 20, bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))', zIndex: 1400,
          width: 46, height: 46, borderRadius: 999, border: 'none', cursor: 'pointer',
          background: 'var(--color-primary)', color: '#0E1209',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
        }}>
        <MessageSquarePlus size={20} />
      </button>

      {open && (
        <div onClick={reset} style={{
          position: 'fixed', inset: 0, zIndex: 1401, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 16,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '100%', maxWidth: 440, background: 'var(--color-bg-card)', color: 'var(--color-text)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 20,
            marginBottom: 'env(safe-area-inset-bottom, 0px)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Send feedback</h2>
              <button onClick={reset} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer' }}><X size={18} /></button>
            </div>

            {done ? (
              <div style={{ textAlign: 'center', padding: '18px 0' }}>
                <div style={{ fontSize: 34, marginBottom: 8 }}>🙏</div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Thanks — we got it.</p>
                <p style={{ margin: '4px 0 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>
                  You can follow the thread under Help → Support.
                </p>
                <button onClick={reset} style={btn(true)}>Done</button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                  {CATS.map(c => (
                    <button key={c.key} onClick={() => setCat(c.key)} style={{
                      padding: '7px 12px', borderRadius: 999, fontSize: 13, cursor: 'pointer',
                      border: `1px solid ${cat === c.key ? 'var(--color-primary)' : 'rgba(255,255,255,0.14)'}`,
                      background: cat === c.key ? 'color-mix(in srgb, var(--color-primary) 18%, transparent)' : 'transparent',
                      color: 'var(--color-text)',
                    }}>{c.emoji} {c.label}</button>
                  ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginRight: 4 }}>Rating</span>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => setRating(rating === n ? 0 : n)} aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, lineHeight: 0 }}>
                      <Star size={22} fill={n <= rating ? 'var(--color-primary)' : 'none'} color={n <= rating ? 'var(--color-primary)' : 'var(--color-text-faint)'} />
                    </button>
                  ))}
                </div>

                <textarea
                  value={body} onChange={e => setBody(e.target.value)} rows={4} autoFocus
                  placeholder="What's working, what's not, what you'd love to see…"
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: 10, borderRadius: 10, resize: 'vertical',
                    border: '1px solid rgba(255,255,255,0.14)', background: 'var(--color-bg)', color: 'var(--color-text)', fontSize: 14,
                  }} />

                {submit.isError && <p style={{ margin: '8px 0 0', fontSize: 12, color: '#E5533C' }}>{apiError(submit.error, 'Could not send feedback')}</p>}

                <button onClick={() => submit.mutate()} disabled={submit.isPending || !body.trim()} style={{ ...btn(true), width: '100%', marginTop: 14, opacity: (submit.isPending || !body.trim()) ? 0.5 : 1 }}>
                  {submit.isPending ? 'Sending…' : 'Send feedback'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  )
}

function btn(primary: boolean): React.CSSProperties {
  return {
    padding: '9px 18px', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: primary ? 'var(--color-primary)' : 'transparent', color: primary ? '#0E1209' : 'var(--color-text)',
  }
}
